"use client";

import React, { useEffect, useMemo, useState } from "react";
import {
  useGoogleDriveBrowser,
  useGoogleDriveCacheControl,
} from "@/lib/hooks/google-drive/use-google-drive-browser";
import { Card, CardContent, LoadingSpinner } from "@wtc/ui";
import { toast } from "sonner";
import { useUniversalAuth } from "@wtc/ui";

import { DriveListView } from "./GoogleDriveBrowser/DriveListView";
import { FileListView } from "./GoogleDriveBrowser/FileListView";
import { GoogleDriveBrowserHeader } from "./GoogleDriveBrowser/GoogleDriveBrowserHeader";
import { GoogleDriveErrorState } from "./GoogleDriveBrowser/GoogleDriveErrorState";
import { GoogleDriveAccessRequiredState } from "./GoogleDriveBrowser/GoogleDriveAccessRequiredState";
import { GoogleDriveAccessBanner } from "./GoogleDriveBrowser/GoogleDriveAccessBanner";
import { GoogleDriveBulkActionBar } from "./GoogleDriveBrowser/GoogleDriveBulkActionBar";
import { GoogleDriveBrowserOverlays } from "./GoogleDriveBrowser/GoogleDriveBrowserOverlays";
import {
  GoogleDriveDropOverlay,
  GoogleDriveFolderPickerBar,
} from "./GoogleDriveBrowser/GoogleDriveFolderPickerBar";
import { useGoogleDriveNavigation } from "./hooks/useGoogleDriveNavigation";
import { useGoogleDriveFileActions } from "./hooks/useGoogleDriveFileActions";
import { useGoogleDriveLoadMore } from "./hooks/useGoogleDriveLoadMore";
import { useGoogleDriveBulkActions } from "./hooks/useGoogleDriveBulkActions";
import { useGoogleDriveDragUpload } from "./hooks/useGoogleDriveDragUpload";
import { useDebounce } from "@/lib/hooks/ui/use-debounce";
import type { GoogleDriveBrowserProps } from "./types";

export type { GoogleDriveBrowserProps } from "./types";

export function GoogleDriveBrowser({
  className = "",
  email,
  isSecondAccount = false,
  initialDriveId,
  initialParentId,
  initialParentName,
  initialBreadcrumbs,
  breadcrumbLoading = false,
  showHeader = true,
  maxHeight = "500px",
  onFileSelect,
  onFolderSelect,
  onPickCurrentFolder,
  allowDownload = true,
  allowView = true,
  compactMode = false,
  allowCreateFolder = true,
  allowUpload = true,
  allowDelete = true,
}: GoogleDriveBrowserProps) {
  const { session } = useUniversalAuth();
  const accessToken = session?.access_token;
  const cacheControl = useGoogleDriveCacheControl();

  const navigation = useGoogleDriveNavigation({
    initialDriveId,
    initialParentId,
    initialParentName,
    initialBreadcrumbs,
    onFolderSelect,
  });

  const isFilesView = navigation.viewMode === "files";

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 300);

  // Reset search when the folder / drive / view changes — the term is scoped to
  // the current folder, so it shouldn't leak across navigation.
  useEffect(() => {
    setSearchInput("");
  }, [navigation.currentParentId, navigation.currentDriveId, navigation.viewMode]);

  const {
    data: result,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useGoogleDriveBrowser({
    enabled: true,
    parentId: navigation.currentParentId,
    driveId: navigation.currentDriveId,
    action: navigation.viewMode === "drives" ? "drives" : "list",
    email,
    isSecondAccount,
    pageToken: undefined,
    pageSize: 100,
    query: isFilesView ? debouncedSearch : undefined,
  });

  const loadMoreState = useGoogleDriveLoadMore({
    result,
    viewMode: navigation.viewMode,
    currentParentId: navigation.currentParentId,
    currentDriveId: navigation.currentDriveId,
    email,
    isSecondAccount,
    accessToken,
  });

  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const fileActions = useGoogleDriveFileActions({
    isSecondAccount,
    accessToken,
    onFileSelect,
  });

  const handleRefresh = React.useCallback(() => {
    cacheControl.forceRefresh({
      parentId: navigation.currentParentId,
      driveId: navigation.currentDriveId,
      action: navigation.viewMode === "drives" ? "drives" : "list",
      email,
      isSecondAccount,
    });
    void refetch();
    toast.success("Drive content refreshed");
  }, [cacheControl, navigation.currentParentId, navigation.currentDriveId, navigation.viewMode, email, isSecondAccount, refetch]);

  const bulk = useGoogleDriveBulkActions({
    isSecondAccount,
    accessToken,
    parentId: navigation.currentParentId,
    driveId: navigation.currentDriveId,
    onAfterChange: handleRefresh,
  });

  const displayFiles = useMemo(
    () => loadMoreState.accumulatedFiles || result?.files || [],
    [loadMoreState.accumulatedFiles, result?.files]
  );

  // Name of the folder currently being browsed (deepest breadcrumb), for the
  // "Select this folder" picker affordance. Falls back to the initial name.
  const currentFolderName = useMemo(() => {
    const last = navigation.breadcrumbs[navigation.breadcrumbs.length - 1];
    return last?.name || initialParentName || "this folder";
  }, [navigation.breadcrumbs, initialParentName]);

  // Clear any selection when navigating to a different folder/view.
  const { clearSelection } = bulk;
  useEffect(() => {
    clearSelection();
  }, [navigation.currentParentId, navigation.currentDriveId, navigation.viewMode, clearSelection]);

  const { uploadFiles } = bulk;
  const handleDroppedFiles = React.useCallback(
    (files: FileList) => {
      void uploadFiles(files);
    },
    [uploadFiles]
  );

  const drag = useGoogleDriveDragUpload({
    enabled: isFilesView && allowUpload && !!result?.hasAccess,
    onFiles: handleDroppedFiles,
  });

  const canGoBack = navigation.breadcrumbs.length > 0 || navigation.viewMode === "files";

  if (error) {
    return (
      <GoogleDriveErrorState
        className={className}
        message={error.message || "Failed to access Google Drive"}
        onRetry={handleRefresh}
      />
    );
  }

  return (
    <Card className={className}>
      {showHeader && (
        <GoogleDriveBrowserHeader
          email={email}
          compactMode={compactMode}
          breadcrumbs={navigation.breadcrumbs}
          breadcrumbLoading={breadcrumbLoading}
          viewMode={navigation.viewMode}
          canGoBack={canGoBack}
          onBack={navigation.goBack}
          onBreadcrumbNavigate={navigation.handleBreadcrumbNavigate}
          onRefresh={handleRefresh}
          isLoading={isLoading}
          isRefetching={isRefetching}
          allowCreateFolder={allowCreateFolder}
          allowUpload={allowUpload}
          onCreateFolder={() => setShowCreateFolderModal(true)}
          onUpload={() => setShowUploadModal(true)}
          showSearch={isFilesView && !!result?.hasAccess}
          searchValue={searchInput}
          onSearchChange={setSearchInput}
        />
      )}

      <CardContent className={compactMode ? "p-4" : ""}>
        <GoogleDriveAccessBanner isSecondAccount={isSecondAccount} />

        {onPickCurrentFolder && isFilesView && !!result?.hasAccess && (
          <GoogleDriveFolderPickerBar
            folderName={currentFolderName}
            folderId={navigation.currentParentId}
            onPick={onPickCurrentFolder}
          />
        )}

        {isFilesView && (
          <GoogleDriveBulkActionBar
            selectedCount={bulk.selectedIds.size}
            allowDownload={allowDownload}
            allowDelete={allowDelete}
            isDownloading={bulk.isDownloadingBulk}
            isDeleting={bulk.isDeletingBulk}
            onDownload={() => void bulk.downloadSelected(displayFiles)}
            onDelete={() => void bulk.deleteSelected(displayFiles)}
            onClear={bulk.clearSelection}
          />
        )}

        {/* Drop-zone wrapper has NO overflow, so the drag overlay's positioning
            ancestor is unclipped (see Z_INDEX_AND_OVERFLOW). The scroll area is
            a sibling-child below. */}
        <div
          className="relative"
          onDragOver={drag.onDragOver}
          onDragLeave={drag.onDragLeave}
          onDrop={drag.onDrop}
        >
          {drag.showOverlay && <GoogleDriveDropOverlay />}

          <div className="space-y-2 overflow-y-auto" style={{ maxHeight }}>
            {bulk.isUploading && (
              <div className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
                <LoadingSpinner className="h-4 w-4" />
                Uploading…
              </div>
            )}
            {isLoading && !isRefetching ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner className="h-6 w-6 mr-2" />
                <span className="text-sm text-muted-foreground">
                  Loading {navigation.viewMode === "drives" ? "shared drives" : "files"}...
                </span>
              </div>
            ) : !result?.hasAccess ? (
              <GoogleDriveAccessRequiredState isSecondAccount={isSecondAccount} />
            ) : navigation.viewMode === "drives" ? (
              <DriveListView
                drives={result?.drives || []}
                onDriveSelect={navigation.handleDriveSelect}
              />
            ) : (
              <FileListView
                files={result?.files || []}
                accumulatedFiles={loadMoreState.accumulatedFiles}
                isDownloading={fileActions.isDownloading}
                allowDownload={allowDownload}
                allowView={allowView}
                onFolderSelect={navigation.handleFolderSelect}
                onFileAction={fileActions.handleFileAction}
                nextPageToken={loadMoreState.nextPageToken}
                onLoadMore={loadMoreState.loadMore}
                selectable={allowDownload || allowDelete}
                selectedIds={bulk.selectedIds}
                onToggleSelect={bulk.toggleSelect}
              />
            )}
          </div>
        </div>
      </CardContent>

      <GoogleDriveBrowserOverlays
        previewFile={fileActions.previewFile}
        isSecondAccount={isSecondAccount}
        parentId={navigation.currentParentId}
        driveId={navigation.currentDriveId}
        createFolderOpen={showCreateFolderModal}
        onCreateFolderOpenChange={setShowCreateFolderModal}
        uploadOpen={showUploadModal}
        onUploadOpenChange={setShowUploadModal}
        onSuccess={handleRefresh}
      />
    </Card>
  );
}
